import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from './store/authStore';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import TicketsPage from './pages/TicketsPage';
import TicketDetailPage from './pages/TicketDetailPage';
import WorkLogsPage from './pages/WorkLogsPage';
import AdminPage from './pages/AdminPage';
import BillingPage from './pages/BillingPage';
import VoicePage from './pages/VoicePage';
import ProfilePage from './pages/ProfilePage';
import AuditPage from './pages/AuditPage';
import OrganizationPage from './pages/OrganizationPage';
import ProjectPage from './pages/ProjectPage';
import MilestonePage from './pages/MilestonePage';
import Layout from './components/Layout';
import ErrorBoundary from './components/ErrorBoundary';

const ApiPlatformPage = lazy(() => import('./pages/ApiPlatformPage'));
const ReportsPage = lazy(() => import('./pages/ReportsPage'));
const IntegrationsPage = lazy(() => import('./pages/IntegrationsPage'));
const IntegrationDetailPage = lazy(() => import('./pages/IntegrationDetailPage'));
const BlocklistPage = lazy(() => import('./pages/BlocklistPage'));
const EmailTemplatesPage = lazy(() => import('./pages/EmailTemplatesPage'));
const SystemSettingsPage = lazy(() => import('./pages/SystemSettingsPage'));
const AttendancePage = lazy(() => import('./pages/AttendancePage'));
const ContactsPage = lazy(() => import('./pages/ContactsPage'));
const LeadsPage = lazy(() => import('./pages/LeadsPage'));
const LeadDetailPage = lazy(() => import('./pages/LeadDetailPage'));
const DashboardBuilderPage = lazy(() => import('./pages/DashboardBuilderPage'));
const BackupsPage = lazy(() => import('./pages/BackupsPage'));
const ProductsPage = lazy(() => import('./pages/ProductsPage'));
const QuotesPage = lazy(() => import('./pages/QuotesPage'));
const QuoteDetailPage = lazy(() => import('./pages/QuoteDetailPage'));
const MeetingsPage = lazy(() => import('./pages/MeetingsPage'));
const CashFlowPage = lazy(() => import('./pages/CashFlowPage'));

function LazyFallback() {
  return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
    </div>
  );
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />;
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const user = useAuthStore((state) => state.user);
  if (!user || user.role !== 'admin') {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}

function ManagerRoute({ children }: { children: React.ReactNode }) {
  const user = useAuthStore((state) => state.user);
  if (!user || !['admin', 'customer_manager'].includes(user.role)) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}

function AgentRoute({ children }: { children: React.ReactNode }) {
  const user = useAuthStore((state) => state.user);
  if (!user || !['admin', 'agent'].includes(user.role)) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}

function InternalRoute({ children }: { children: React.ReactNode }) {
  const user = useAuthStore((state) => state.user);
  if (!user || user.role === 'customer_viewer') {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<LoginPage />} />

        <Route
          path="/"
          element={
            <ProtectedRoute>
              <ErrorBoundary>
                <Layout />
              </ErrorBoundary>
            </ProtectedRoute>
          }
        >
          <Route index element={<Suspense fallback={<LazyFallback />}><DashboardBuilderPage /></Suspense>} />
          <Route path="dashboard-legacy" element={<DashboardPage />} />
          <Route path="tickets" element={<TicketsPage />} />
          <Route path="tickets/:id" element={<TicketDetailPage />} />
          <Route path="organizations/:id" element={<OrganizationPage />} />
          <Route path="projects/:id" element={<ProjectPage />} />
          <Route path="milestones/:id" element={<MilestonePage />} />
          <Route
            path="work-logs"
            element={
              <InternalRoute>
                <WorkLogsPage />
              </InternalRoute>
            }
          />
          <Route
            path="billing"
            element={
              <ManagerRoute>
                <BillingPage />
              </ManagerRoute>
            }
          />
          <Route
            path="voice"
            element={
              <AgentRoute>
                <VoicePage />
              </AgentRoute>
            }
          />
          <Route
            path="contacts"
            element={
              <AgentRoute>
                <Suspense fallback={<LazyFallback />}><ContactsPage /></Suspense>
              </AgentRoute>
            }
          />
          <Route
            path="meetings"
            element={
              <AgentRoute>
                <Suspense fallback={<LazyFallback />}><MeetingsPage /></Suspense>
              </AgentRoute>
            }
          />
          <Route
            path="leads"
            element={
              <AgentRoute>
                <Suspense fallback={<LazyFallback />}><LeadsPage /></Suspense>
              </AgentRoute>
            }
          />
          <Route
            path="leads/:id"
            element={
              <AgentRoute>
                <Suspense fallback={<LazyFallback />}><LeadDetailPage /></Suspense>
              </AgentRoute>
            }
          />
          <Route
            path="reports"
            element={
              <InternalRoute>
                <Suspense fallback={<LazyFallback />}><ReportsPage /></Suspense>
              </InternalRoute>
            }
          />
          <Route
            path="attendance"
            element={
              <AgentRoute>
                <Suspense fallback={<LazyFallback />}><AttendancePage /></Suspense>
              </AgentRoute>
            }
          />
          <Route path="profile" element={<ProfilePage />} />
          <Route
            path="audit"
            element={
              <ManagerRoute>
                <AuditPage />
              </ManagerRoute>
            }
          />
          <Route
            path="admin"
            element={
              <AgentRoute>
                <AdminPage />
              </AgentRoute>
            }
          />
          <Route
            path="cashflow"
            element={
              <AdminRoute>
                <Suspense fallback={<LazyFallback />}><CashFlowPage /></Suspense>
              </AdminRoute>
            }
          />
          <Route
            path="api-platform"
            element={
              <AdminRoute>
                <Suspense fallback={<LazyFallback />}><ApiPlatformPage /></Suspense>
              </AdminRoute>
            }
          />
          <Route
            path="integrations"
            element={
              <AgentRoute>
                <Suspense fallback={<LazyFallback />}><IntegrationsPage /></Suspense>
              </AgentRoute>
            }
          />
          <Route
            path="integrations/:id"
            element={
              <AgentRoute>
                <Suspense fallback={<LazyFallback />}><IntegrationDetailPage /></Suspense>
              </AgentRoute>
            }
          />
          <Route
            path="blocklist"
            element={
              <AdminRoute>
                <Suspense fallback={<LazyFallback />}><BlocklistPage /></Suspense>
              </AdminRoute>
            }
          />
          <Route
            path="email-templates"
            element={
              <AdminRoute>
                <Suspense fallback={<LazyFallback />}><EmailTemplatesPage /></Suspense>
              </AdminRoute>
            }
          />
          <Route
            path="settings/system"
            element={
              <AdminRoute>
                <Suspense fallback={<LazyFallback />}><SystemSettingsPage /></Suspense>
              </AdminRoute>
            }
          />
          <Route
            path="settings/backups"
            element={
              <AdminRoute>
                <Suspense fallback={<LazyFallback />}><BackupsPage /></Suspense>
              </AdminRoute>
            }
          />
          <Route
            path="products"
            element={
              <AdminRoute>
                <Suspense fallback={<LazyFallback />}><ProductsPage /></Suspense>
              </AdminRoute>
            }
          />
          <Route
            path="quotes"
            element={
              <AgentRoute>
                <Suspense fallback={<LazyFallback />}><QuotesPage /></Suspense>
              </AgentRoute>
            }
          />
          <Route
            path="quotes/:id"
            element={
              <AgentRoute>
                <Suspense fallback={<LazyFallback />}><QuoteDetailPage /></Suspense>
              </AgentRoute>
            }
          />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
